import dayjs from 'dayjs';

import { ICustomer } from 'models/customer.model';
import { Transporter } from 'models/transporter.model';
import { IPaymentTransaction } from 'models/payment-transaction.model';
import { InvoiceStatusEnum } from './enums/invoice-status.enum';
import { BasketOrderStatus, LIST_BASKET_ORDER_STATUS } from './basket-order-status.model';
import { LIST_PAYMENT_METHOD, PaymentMethod } from './payment-method.model';
import { ColorEnum } from './enums/color.enum';

export class BasketOrder {
  public id?: number;
  public status: InvoiceStatusEnum | BasketOrderStatus;
  public priceHt?: number;
  public transportPrice?: number;
  public insurance?: number;
  public vat?: number;
  public price?: number;
  public totalPallets?: number;
  public totalParcels?: number;
  public totalUms?: number;
  public billDate?: dayjs.Dayjs;
  public billAddressFullname?: string;
  public billAddressStreetNumber?: string;
  public billAddressStreet?: string;
  public billAddressZipCode?: string;
  public billAddressCity?: string;
  public billAddressCountry?: string;
  public billAddressAdditionals?: string;
  public vatNumber?: string;
  public carbonFootprint?: number;
  public totalWeight?: number;
  public dueDate?: dayjs.Dayjs;
  public gazoleTaxRate?: number;
  public gazoleTaxCurrentValue?: number;
  public gazoleTaxReferenceValue?: number;
  public gazoleTax?: number;
  public billNumber?: string;
  public paymentMethod: string | PaymentMethod;
  public paymentTransactionId?: number;
  public billStorage?: string;
  public totalVolume?: number;
  public billingQuantity?: number;
  public paymentCardAlias?: string;
  public incidentAmount?: number;
  public customer?: ICustomer;
  public transporter: Transporter;
  public currentOwner?: ICustomer;
  public paymentTransactions: IPaymentTransaction[] = [];
  public nbRelaunch?: number;

  constructor(data: any = {}) {
    Object.assign(this, data);
    this.billDate = data.billDate ? dayjs(data.billDate) : undefined;
    this.dueDate = data.dueDate ? dayjs(data.dueDate) : undefined;
    this.transporter = data.transporter ? new Transporter(data.transporter) : new Transporter();
    this.status = data.status ? LIST_BASKET_ORDER_STATUS[data.status] : LIST_BASKET_ORDER_STATUS[InvoiceStatusEnum.INIT];
    this.paymentMethod = data.paymentMethod ? LIST_PAYMENT_METHOD[data.paymentMethod] : undefined;
  }

  public statusBadgeOpts(): { labelKey?: string; color: ColorEnum; emoji?: string; icon?: string; iconTooltipKey?: string; iconTooltipParams?: any } {
    const statusObj: BasketOrderStatus = this.status instanceof BasketOrderStatus ? this.status : LIST_BASKET_ORDER_STATUS[this.status];
    const paymentObj: PaymentMethod = this.paymentMethod instanceof PaymentMethod ? this.paymentMethod : LIST_PAYMENT_METHOD[this.paymentMethod];

    // Par défaut
    let labelKey = statusObj.translationKey;
    let color = statusObj.color;
    let icon: string | undefined;
    let iconTooltipKey: string | undefined;
    let iconTooltipParams: any;
    let emoji = statusObj.emoji;

    // Règle prioritaire : à payer
    const isDeferredBilled = statusObj.code === LIST_BASKET_ORDER_STATUS.BILLED.code && paymentObj.code === LIST_PAYMENT_METHOD.INIT_TRANSFERT_DEFERRED.code;

    if (isDeferredBilled) {
      labelKey = 'INVOICE_STATUS.TO_BE_PAID';
      color = ColorEnum.WARNING;
      emoji = '💸';
    }

    // Icône pour statut en retard avec relance
    const isLateWithRelaunch = (statusObj.code === LIST_BASKET_ORDER_STATUS.LATE.code && this.nbRelaunch) || 0 > 0;

    if (isLateWithRelaunch) {
      icon = 'info-circle';
      iconTooltipKey = 'TOOLTIP_MESSAGE.INFORMATION_ON_DUNNING_EMAILS';
      iconTooltipParams = this.getLateTooltipMessage();
    }

    return { labelKey, color, emoji, icon, iconTooltipKey, iconTooltipParams };
  }

  private getLateTooltipMessage(): { relaunchDate?: string; relaunchNumber: number } {
    const relaunchNumber = this.nbRelaunch ? this.nbRelaunch - 2 : 0;

    switch (this.nbRelaunch) {
      case 4:
        return {
          relaunchDate: this.dueDate?.add(5, 'days').format('DD/MM/YYYY'),
          relaunchNumber,
        };
      case 5:
        return {
          relaunchDate: this.dueDate?.add(6, 'days').format('DD/MM/YYYY'),
          relaunchNumber,
        };
      default:
        return {
          relaunchDate: this.dueDate?.format('DD/MM/YYYY'),
          relaunchNumber,
        };
    }
  }
}
